import {useCallback, useState} from "react";
import {API_HOST} from "./apiHost";

const BANNED_DOMAINS = new Set([
  "btinternet.com",
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.fr",
  "yahoo.co.uk",
  "yahoo.co.in",
  "orange.fr",
  "wanadoo.fr",
  "hotmail.com",
  "hotmail.co.uk",
  "outlook.com",
  "aol.com",
  "icloud.com",
  "proton.me",
  "protonmail.com",
  "msn.com",
  "live.com",
]);

function isBusinessEmail(email: string): boolean {
  const domain = email.split("@")[1].toLowerCase();
  return !BANNED_DOMAINS.has(domain);
}

type useSubscribeT = {
  email?: string;
  setEmail: (v?: string) => void;
  error?: string;
  isLoading: boolean;
  success: boolean;
  onSubmit: React.FormEventHandler;
};

export default function useSubscribe(): useSubscribeT {
  const [email, _setEmail] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const setEmail = useCallback(
    (email?: string) => {
      setError(undefined);
      _setEmail(email);
    },
    [setError, _setEmail],
  );

  const onSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!email) {
        setError("Please enter your email");
        return;
      }

      if (email.indexOf("@") < 0) {
        setError("Please enter your email");
        return;
      }

      if (!isBusinessEmail(email)) {
        setError("Please use a business email address");
        return;
      }

      setIsLoading(true);
      setError(undefined);

      try {
        const resp = await fetch(`${API_HOST}/support_emails/newsletter_subscribe`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({email}),
        });

        if (resp.ok) {
          setSuccess(true);
          setError(undefined);
          setEmail(undefined);
        } else {
          const json = await resp.json();
          if (json.email) {
            setError(json.email[0].message);
          } else {
            setError("An unknown error occurred");
          }
        }
      } catch (e) {
        setError("An unknown error occurred");
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    },
    [email, setEmail, setError, setSuccess, setIsLoading],
  );

  return {email, setEmail, error, isLoading, onSubmit, success};
}
